import React, { useState } from 'react';

const ALLOWED_EXTS  = ['pdf', 'doc', 'docx'];
const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_SIZE = 2 * 1024 * 1024;

export default function StepPersonalInfo({ data, errors, update }) {
  const [fileError, setFileError] = useState('');
  const [inputKey, setInputKey] = useState(0);

  const handleRemove = (e) => {
    e.stopPropagation();
    e.preventDefault();
    update('resume', null);
    update('resume_link', '');
    setFileError('');
    setInputKey(k => k + 1); // remounts the file input, clearing its value
  };

  // Validate locally and store file — actual upload happens at submit time
  // so branch_name and company_name are available for server folder/file naming
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileError('');
    const ext = file.name.split('.').pop().toLowerCase();

    if (!ALLOWED_EXTS.includes(ext) || !ALLOWED_TYPES.includes(file.type)) {
      setFileError('Only PDF, DOC, DOCX files are allowed');
      return;
    }
    if (file.size > MAX_SIZE) {
      setFileError('File size must be under 2MB');
      return;
    }

    update('resume', file);
    update('resume_link', '');
  };


  return (
    <div>
      <h2 className="section-title">Personal Info</h2>

      {/* First Name + Last Name */}
      <div className="form-row">
        <div className="form-group">
          <label>First Name <span style={{ color: '#e53e3e' }}>*</span></label>
          <input
            className="form-control"
            value={data.first_name}
            onChange={e => update('first_name', e.target.value)}
          />
          {errors.first_name && <div className="error-text">{errors.first_name}</div>}
        </div>
        <div className="form-group">
          <label>Last Name <span style={{ color: '#e53e3e' }}>*</span></label>
          <input
            className="form-control"
            value={data.last_name}
            onChange={e => update('last_name', e.target.value)}
          />
          {errors.last_name && <div className="error-text">{errors.last_name}</div>}
        </div>
      </div>

      {/* Father Name + Email */}
      <div className="form-row">
        <div className="form-group">
          <label>Father Name <span style={{ color: '#e53e3e' }}>*</span></label>
          <input
            className="form-control"
            value={data.father_name}
            onChange={e => update('father_name', e.target.value)}
          />
          {errors.father_name && <div className="error-text">{errors.father_name}</div>}
        </div>
        <div className="form-group">
          <label>Email Address <span style={{ color: '#e53e3e' }}>*</span></label>
          <input
            type="email"
            className="form-control"
            value={data.email}
            onChange={e => update('email', e.target.value)}
          />
          {errors.email && <div className="error-text">{errors.email}</div>}
        </div>
      </div>

      {/* Phone + Gender */}
      <div className="form-row">
        <div className="form-group">
          <label>Phone (03123456789) <span style={{ color: '#e53e3e' }}>*</span></label>
          <input
            className="form-control"
            placeholder="03123456789"
            value={data.phone}
            onChange={e => update('phone', e.target.value.replace(/\D/g, '').slice(0, 11))}
          />
          {errors.phone && <div className="error-text">{errors.phone}</div>}
        </div>
        <div className="form-group">
          <label>Gender <span style={{ color: '#e53e3e' }}>*</span></label>
          <select
            className="form-control"
            value={data.gender}
            onChange={e => update('gender', e.target.value)}
          >
            <option value="">Select Gender</option>
            <option value="331">Male</option>
            <option value="332">Female</option>
          </select>
          {errors.gender && <div className="error-text">{errors.gender}</div>}
        </div>
      </div>

      {/* CNIC */}
      <div className="form-group">
        <label>CNIC <small>(Optional — 13 digits without dashes)</small></label>
        <input
          className="form-control"
          placeholder="8213056788900"
          value={data.cnic}
          onChange={e => update('cnic', e.target.value.replace(/\D/g, '').slice(0, 13))}
        />
        {errors.cnic && <div className="error-text">{errors.cnic}</div>}
      </div>

      {/* Address */}
      <div className="form-group">
        <label>Residential Address</label>
        <textarea
          className="form-control"
          rows="2"
          value={data.address}
          onChange={e => update('address', e.target.value)}
        />
        {errors.address && <div className="error-text">{errors.address}</div>}
      </div>

      {/* CV Upload */}
      <div className="form-group">
        <label>
          Upload Resume <small>(PDF, DOCX — Max 2MB)</small>
        </label>

        {/* When a file is selected: show status card with Remove button — NO file input underneath */}
        {(data.resume || data.resume_link) ? (
          <div style={styles.fileCard}>
            <div style={styles.statusRow}>
              <span style={{ fontSize: 18 }}>📄</span>
              <span style={{ fontSize: 13, color: '#374151', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {data.resume?.name || 'CV uploaded'}
              </span>
              {data.resume_link && (
                <a href={data.resume_link} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#2563eb', flexShrink: 0 }}>
                  View
                </a>
              )}
            </div>
            <button onClick={handleRemove} style={styles.removeBtn}>✕ Remove</button>
          </div>
        ) : (
          /* When no file: show clickable upload wrapper */
          <div
            className="file-upload-wrapper"
            style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
          >
            <input
              key={inputKey}
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              style={{ cursor: 'pointer' }}
            />
            <div className="file-upload-label">
              <span style={{ fontSize: 13, color: '#9CA3AF' }}>
                Click to upload — PDF, DOC, DOCX · Max 2MB
              </span>
            </div>
          </div>
        )}

        {fileError && (
          <div className="error-text" style={{ marginTop: 4 }}>{fileError}</div>
        )}

        {errors.resume && !fileError && (
          <div className="error-text">{errors.resume}</div>
        )}
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────
// Inline styles
// ─────────────────────────────────────────────
const styles = {
  fileCard: {
    border: '1px solid #d2d8dd',
    borderRadius: 6,
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    background: '#f9fafb',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    overflow: 'hidden',
  },
  removeBtn: {
    flexShrink: 0,
    background: 'none',
    border: '1px solid #fca5a5',
    borderRadius: 4,
    color: '#dc2626',
    fontSize: 11,
    padding: '3px 10px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
};